import type { EditorBody } from "@/components/document-editor"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import AppLayout from "@/layouts/AppLayout"
import { uploadEditorFile } from "@/lib/upload"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { IconArrowsMoveVertical, IconDots, IconFilePlus, IconTrash, IconUserPlus } from "@tabler/icons-react"
import { Suspense, lazy, useEffect, useRef, useState } from "react"
import { DocumentTree } from "./components/DocumentTree"
import { MoveDialog } from "./components/MoveDialog"
import { ShareDialog } from "./components/ShareDialog"
import type { DocumentDetail, ShareableUser, TreeNode } from "./types"

interface Props {
  [key: string]: unknown
  tree: TreeNode[]
  document: DocumentDetail
  shareable_users: ShareableUser[]
}

const DocumentEditor = lazy(() =>
  import("@/components/document-editor").then((module) => ({ default: module.DocumentEditor })),
)

const AUTOSAVE_DELAY = 800

export default function DocumentsShow() {
  const { tree, document: doc, shareable_users } = usePage<Props>().props

  const [title, setTitle] = useState(doc.title)
  const [saving, setSaving] = useState(false)
  const [savedOnce, setSavedOnce] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<{ title: string; body?: EditorBody }>({ title: doc.title })

  const loadedId = useRef(doc.id)

  useEffect(() => {
    if (loadedId.current === doc.id) return

    loadedId.current = doc.id
    setTitle(doc.title)
    setSavedOnce(false)
    pending.current = { title: doc.title }
  }, [doc.id, doc.title])

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const scheduleSave = () => {
    if (!doc.can_edit) return
    if (timer.current) clearTimeout(timer.current)

    timer.current = setTimeout(() => {
      router.patch(
        `/documents/${doc.id}`,
        { document: pending.current },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["document", "tree"],
          onStart: () => setSaving(true),
          onSuccess: () => setSavedOnce(true),
          onFinish: () => setSaving(false),
        },
      )
    }, AUTOSAVE_DELAY)
  }

  const onTitleChange = (value: string) => {
    setTitle(value)
    pending.current = { ...pending.current, title: value }
    scheduleSave()
  }

  const onBodyChange = (body: EditorBody) => {
    pending.current = { ...pending.current, body }
    scheduleSave()
  }

  return (
    <>
      <Head title={doc.title} />

      <div className="flex flex-1">
        <aside className="bg-sidebar hidden w-64 shrink-0 border-r p-3 lg:block">
          <Button variant="outline" size="sm" className="mb-3 w-full" onClick={() => router.post("/documents", {})}>
            <IconFilePlus className="size-4" />
            New page
          </Button>
          <DocumentTree nodes={tree} activeId={doc.id} />
        </aside>

        <main className="min-w-0 flex-1 px-8 py-10">
          <div className="document-surface mx-auto max-w-3xl">
            <div className="document-gutter mb-2 flex items-center justify-between gap-2">
              <div className="min-w-0 truncate text-xs text-muted-foreground">
                {!doc.is_root ? (
                  <Link href={`/documents/${doc.share_target.id}`} className="truncate hover:underline">
                    {doc.share_target.title}
                  </Link>
                ) : (
                  doc.last_edited_by && `Edited by ${doc.last_edited_by.name}`
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-muted-foreground" aria-live="polite">
                  {saving ? "Saving…" : savedOnce ? "Saved" : ""}
                </span>

                {doc.can_share && (
                  <Button variant="outline" size="sm" onClick={() => setShareOpen(true)}>
                    <IconUserPlus className="size-4" />
                    Share
                  </Button>
                )}

                {doc.can_edit && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="Page actions">
                        <IconDots className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => router.post("/documents", { parent_id: doc.id })}>
                        <IconFilePlus className="size-4" />
                        New subpage
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setMoveOpen(true)}>
                        <IconArrowsMoveVertical className="size-4" />
                        Move to…
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => router.patch(`/documents/${doc.id}/archive`)}>
                        <IconTrash className="size-4" />
                        Move to trash
                      </DropdownMenuItem>
                      {doc.can_destroy && (
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => {
                            if (window.confirm("Delete this page and everything nested under it?")) {
                              router.delete(`/documents/${doc.id}`)
                            }
                          }}
                        >
                          <IconTrash className="size-4" />
                          Delete permanently
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {doc.archived && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-dashed p-3">
                <p className="text-sm text-muted-foreground">This page is in the trash.</p>
                <Button size="sm" variant="outline" onClick={() => router.patch(`/documents/${doc.id}/restore`)}>
                  Restore
                </Button>
              </div>
            )}

            <Input
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              readOnly={!doc.can_edit}
              aria-label="Page title"
              placeholder="Untitled"
              className="document-title mb-4 h-auto border-0 bg-transparent pr-0 !text-3xl font-semibold shadow-none focus-visible:ring-0 dark:bg-transparent"
            />

            {/* Keyed on the document id: the editor reads its initial content once, so it
                has to be rebuilt when a different page is opened. */}
            <Suspense fallback={<Skeleton className="h-64 w-full" />}>
              <DocumentEditor
                key={doc.id}
                initialBody={doc.body}
                editable={doc.can_edit}
                onChange={onBodyChange}
                uploadFile={(file) => uploadEditorFile(`/documents/${doc.id}/files`, file)}
              />
            </Suspense>
          </div>
        </main>
      </div>

      {doc.can_share && (
        <ShareDialog open={shareOpen} onOpenChange={setShareOpen} document={doc} shareableUsers={shareable_users} />
      )}

      {doc.can_edit && <MoveDialog open={moveOpen} onOpenChange={setMoveOpen} document={doc} nodes={tree} />}
    </>
  )
}

DocumentsShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
